import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import type { StalledAdopter } from '../../lib/types';
import {
  rescueTextStalledAdoptersMutation,
  type RescueTextStalledAdoptersData,
  type StalledReminderVariables,
} from '../../lib/rescueQueries';
import {
  canText,
  daysSince,
  newReminderRequestId,
  orderForTexting,
  splitSmsTemplate,
} from '../../lib/stalledReminders';
import { formatLastTextReminder, formatPhoneNumber } from '../../lib/utils';
import useStalledAdopters from '../../hooks/useStalledAdopters';
import { trackStalledBulkSmsSent, trackStalledTextAllClicked } from '../../analytics';
import { useToast } from '../../components/Toast/ToastContext';
import Modal, { ModalTitle, ModalSubtitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import FiNumberNotice from './FiNumberNotice';
import LockIcon from './LockIcon';
import styles from './Devices.module.scss';
import r from './StalledReminders.module.scss';

/** A reminder sent within the last week is flagged in the list, since those are the people to leave out. */
const RECENT_REMINDER_DAYS = 7;

const TOKEN_LABELS = { adopterFirstName: 'Adopter first name', petName: 'Pet name' } as const;

function texts(count: number) {
  return `${count} ${count === 1 ? 'text' : 'texts'}`;
}

function sendSummary(result: RescueTextStalledAdoptersData['rescueTextStalledAdopters'], selectedCount: number) {
  const { sentCount, failedCount, optedOutCount } = result;
  if (!failedCount && !optedOutCount) {
    return `${texts(sentCount)} sent`;
  }
  return [
    `Sent ${sentCount} of ${texts(selectedCount)}.`,
    optedOutCount > 0 && `${optedOutCount} ${optedOutCount === 1 ? 'adopter has' : 'adopters have'} opted out.`,
    failedCount > 0 && `${failedCount} couldn't be sent.`,
  ]
    .filter(Boolean)
    .join(' ');
}

function keyOf(adopter: StalledAdopter) {
  return adopter.transferIds[0];
}

function SmsTemplateText({ template }: { template: string }) {
  return (
    <>
      {splitSmsTemplate(template).map((part, index) =>
        'token' in part ? (
          <span key={index} className={r.token}>
            {TOKEN_LABELS[part.token]}
          </span>
        ) : (
          <React.Fragment key={index}>{part.text}</React.Fragment>
        ),
      )}
    </>
  );
}

function LastText({ lastTextReminderAt }: { lastTextReminderAt: string | null }) {
  if (!lastTextReminderAt) {
    return <span className={`${r.recipientLastText} ${r.recipientLastTextNever}`}>Never</span>;
  }
  const recent = daysSince(lastTextReminderAt) <= RECENT_REMINDER_DAYS;
  return (
    <span className={`${r.recipientLastText} ${recent ? r.recipientLastTextRecent : ''}`}>
      {formatLastTextReminder(lastTextReminderAt)}
    </span>
  );
}

function recipientPhoneText(adopter: StalledAdopter) {
  if (!adopter.phoneNumber) {
    return 'No phone on file';
  }
  return adopter.smsOptedOut ? 'Opted out' : formatPhoneNumber(adopter.phoneNumber);
}

function petsAndAge(adopter: StalledAdopter) {
  const days = daysSince(adopter.transferredAt);
  const pets = adopter.petNames.join(', ') || 'Pet name not on file';
  return `${pets} · stalled ${days} ${days === 1 ? 'day' : 'days'}`;
}

interface TextAllStalledModalProps {
  /** The modal mounts per opening, so every opening starts on the compose step with everyone checked. */
  onClose: () => void;
}

/**
 * Text all: the fixed stalled reminder, sent from Fi's number to every stalled adopter the rescue
 * leaves checked. Covers the whole Stalled list, not the page of it the table is showing.
 */
export default function TextAllStalledModal({ onClose }: TextAllStalledModalProps) {
  const { showToast } = useToast();
  const { adopters, smsTemplate, loading, error, refetch } = useStalledAdopters((loaded) => {
    trackStalledTextAllClicked(loaded.filter(canText).length, loaded.length);
  });
  const [send, { loading: sending }] = useMutation<RescueTextStalledAdoptersData, StalledReminderVariables>(
    rescueTextStalledAdoptersMutation,
    { refetchQueries: 'active' },
  );
  const [step, setStep] = useState<'compose' | 'confirm'>('compose');
  // Everyone reachable starts checked, so what the rescue changes is who to leave out.
  const [unchecked, setUnchecked] = useState<ReadonlySet<string>>(new Set());
  const [requestId, setRequestId] = useState<string | null>(null);

  const ordered = orderForTexting(adopters);
  const reachable = ordered.filter(canText);
  const selected = reachable.filter((adopter) => !unchecked.has(keyOf(adopter)));
  const optedOutCount = ordered.filter((adopter) => adopter.phoneNumber && adopter.smsOptedOut).length;
  const noPhoneCount = ordered.filter((adopter) => !adopter.phoneNumber).length;
  const allSelected = reachable.length > 0 && selected.length === reachable.length;

  function toggle(adopter: StalledAdopter) {
    const key = keyOf(adopter);
    setUnchecked((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function toggleAll() {
    setUnchecked(allSelected ? new Set(reachable.map(keyOf)) : new Set());
  }

  function handleReview() {
    // A new id per review: going back and changing who is checked makes it a different send.
    setRequestId(newReminderRequestId());
    setStep('confirm');
  }

  async function handleSend() {
    if (!requestId) {
      return;
    }
    try {
      const { data } = await send({
        variables: { input: { transferIds: selected.flatMap((adopter) => adopter.transferIds), requestId } },
      });
      trackStalledBulkSmsSent(selected.length, reachable.length, ordered.length);
      const result = data?.rescueTextStalledAdopters;
      showToast(result ? sendSummary(result, selected.length) : `${texts(0)} sent`);
      onClose();
    } catch (err) {
      showToast(`Failed to send texts: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  if (loading || error || !smsTemplate || ordered.length === 0) {
    let message = 'No stalled adopters to text right now.';
    if (loading) {
      message = 'Loading stalled adopters…';
    } else if (error) {
      message = "Couldn't load your stalled adopters.";
    }
    return (
      <Modal open onClose={onClose} size="wide">
        <ModalTitle>Text stalled adopters</ModalTitle>
        <p className={r.statusMessage}>{message}</p>
        <ModalActions>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {error && (
            <Button variant="green" onClick={() => refetch()}>
              Try again
            </Button>
          )}
        </ModalActions>
      </Modal>
    );
  }

  if (step === 'confirm' && selected.length > 0) {
    const preview = selected[0];
    const uncheckedCount = reachable.length - selected.length;
    const leftOut = [
      optedOutCount > 0 && `${optedOutCount} opted out`,
      noPhoneCount > 0 && `${noPhoneCount} with no phone number`,
      uncheckedCount > 0 && `${uncheckedCount} unchecked`,
    ].filter(Boolean);
    return (
      <Modal open onClose={sending ? () => {} : onClose} size="wide" dismissOnBackdropClick={false}>
        <ModalTitle>Send {texts(selected.length)}?</ModalTitle>
        <ModalSubtitle>Texts go out right away and can't be recalled.</ModalSubtitle>
        <dl className={styles.confirmDetails}>
          <div>
            <dt>Recipients</dt>
            <dd>
              {selected.length} stalled {selected.length === 1 ? 'adopter' : 'adopters'}
            </dd>
          </div>
          {leftOut.length > 0 && (
            <div>
              <dt>Not included</dt>
              <dd>{leftOut.join(' · ')}</dd>
            </div>
          )}
          <div>
            <dt>Sent from</dt>
            <dd>Fi's text number · adopters can reply STOP</dd>
          </div>
        </dl>
        <div className={r.bubbleWrap}>
          <div className={r.bubbleHeading}>Preview · {preview.displayName}</div>
          <div className={r.bubble}>{preview.smsPreview}</div>
          <div className={r.bubbleFoot}>Every adopter sees their own first name and their pet's name.</div>
        </div>
        <ModalActions>
          <Button variant="ghost" onClick={() => setStep('compose')} disabled={sending}>
            Back
          </Button>
          <Button variant="green" onClick={handleSend} disabled={sending}>
            {sending ? 'Sending…' : `Send ${texts(selected.length)}`}
          </Button>
        </ModalActions>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} size="wide" dismissOnBackdropClick={false}>
      <ModalTitle>Text stalled adopters</ModalTitle>
      <ModalSubtitle>
        <strong>
          {reachable.length} of {ordered.length}
        </strong>{' '}
        reachable by text
        {optedOutCount > 0 && ` · ${optedOutCount} opted out`}
        {noPhoneCount > 0 && ` · ${noPhoneCount} ${noPhoneCount === 1 ? 'has' : 'have'} no phone number on file`}. Each
        adopter gets their own text.
      </ModalSubtitle>

      <div className={r.recipientList}>
        <label className={r.recipientHeader}>
          <input
            type="checkbox"
            className={r.checkbox}
            checked={allSelected}
            ref={(input) => {
              if (input) {
                input.indeterminate = selected.length > 0 && !allSelected;
              }
            }}
            onChange={toggleAll}
            disabled={reachable.length === 0}
          />
          <span>
            Select all <span className={r.recipientHeaderCount}>· {selected.length} selected</span>
          </span>
          <span className={r.columnLabel}>Last Text Reminder</span>
          <span className={r.columnLabel}>Phone</span>
        </label>
        <div className={r.recipientScroll}>
          {ordered.map((adopter) => {
            const reachableAdopter = canText(adopter);
            return (
              <label
                key={keyOf(adopter)}
                className={reachableAdopter ? r.recipientRow : `${r.recipientRow} ${r.recipientRowDisabled}`}
              >
                <input
                  type="checkbox"
                  className={r.checkbox}
                  checked={reachableAdopter && !unchecked.has(keyOf(adopter))}
                  disabled={!reachableAdopter}
                  onChange={() => toggle(adopter)}
                  aria-label={adopter.displayName}
                />
                <span>
                  <span className={r.recipientName}>{adopter.displayName}</span>
                  <span className={r.recipientHint}>{petsAndAge(adopter)}</span>
                </span>
                {adopter.phoneNumber ? (
                  <LastText lastTextReminderAt={adopter.lastTextReminderAt} />
                ) : (
                  <span className={r.recipientLastText}>—</span>
                )}
                <span className={r.recipientPhone}>{recipientPhoneText(adopter)}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div className={r.fieldLabel}>
        <span className={r.fieldLabelLocked}>
          <LockIcon className={r.fieldLabelLock} />
          Fixed message
        </span>{' '}
        <span className={r.fieldLabelAux}>Can't be edited · first name and pet name filled in</span>
      </div>
      <div className={r.smsFixed}>
        <SmsTemplateText template={smsTemplate} />
      </div>
      <div className={r.smsFixedNote}>
        Every text ends with an opt-out line naming your rescue. It can't be removed.
      </div>
      <FiNumberNotice />

      <ModalActions>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleReview} disabled={selected.length === 0}>
          {selected.length > 0 ? `Review ${texts(selected.length)}` : 'Review texts'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
